import { SVGProps } from 'react'

import { websitePaths } from 'domains/website/website.paths'

import { AnalyticsIcon } from 'components/pages/@shared/icons/analytics'
import { BranchingIcon } from 'components/pages/@shared/icons/branching'
import { ChangelogIcon } from 'components/pages/@shared/icons/changelog'
import { CliIcon } from 'components/pages/@shared/icons/cli'
import { DocumentationIcon } from 'components/pages/@shared/icons/documentation'
import { EdgeCachingIcon } from 'components/pages/@shared/icons/edge-caching'
import { ExamplesIcon } from 'components/pages/@shared/icons/examples'
import { GithubIcon } from 'components/pages/@shared/icons/github'
import { GlobeIcon } from 'components/pages/@shared/icons/globe'
import { GuidesIcon } from 'components/pages/@shared/icons/guides'
import { QuickstartIcon } from 'components/pages/@shared/icons/quickstart'
import { ShieldCheckIcon } from 'components/pages/@shared/icons/shield-check'
import { ShieldIcon } from 'components/pages/government/icons/shield'

export type NavbarLinkProps = {
  name: string
  href: string
  description?: string
  icon?: (props: SVGProps<SVGSVGElement>) => JSX.Element
}

const useCaseLinks: NavbarLinkProps[] = [
  {
    href: websitePaths.commerce,
    name: 'Commerce',
  },
  {
    href: websitePaths.mobile,
    name: 'Mobile Apps',
  },
  {
    href: websitePaths.web3,
    name: 'Blockchain and web3',
  },
  {
    href: websitePaths.bff,
    name: 'Backend for Frontend',
  },
  {
    href: websitePaths.ai,
    name: 'AI',
  },
  {
    href: websitePaths.logistics,
    name: 'Logistics',
  },
]

const alternativeLinks: NavbarLinkProps[] = [
  {
    href: websitePaths.apolloServer,
    name: 'Apollo Server alternative',
  },
  {
    href: websitePaths.appSync,
    name: 'AppSync alternative',
  },
  {
    href: websitePaths.hasura,
    name: 'Hasura alternative',
  },
  {
    href: websitePaths.graphqlYoga,
    name: 'GraphQL Yoga alternative',
  },
]

const featuresLinks: NavbarLinkProps[] = [
  {
    name: 'GraphQL Gateway',
    href: websitePaths.edgeGateway,
    description: 'High performance GraphQL APIs at the edge',
    icon: GlobeIcon,
  },
  {
    name: 'Edge Caching',
    href: websitePaths.edgeCaching,
    description: 'Reduce latency and improve conversions globally',
    icon: EdgeCachingIcon,
  },
  {
    name: 'Auth',
    href: websitePaths.auth,
    description: 'Secure your GraphQL API seamlessly',
    icon: ShieldCheckIcon,
  },
  {
    name: 'CLI',
    href: websitePaths.cli,
    description: 'Local development with batteries included',
    icon: CliIcon,
  },
  {
    name: 'Branching',
    href: websitePaths.branching,
    description: 'Collaborate on changes with your team',
    icon: BranchingIcon,
  },
  {
    name: 'Analytics',
    href: websitePaths.analytics,
    description: 'Real-time GraphQL insights',
    icon: AnalyticsIcon,
  },
]

const dropdownLinks: NavbarLinkProps[] = [
  {
    name: 'Quickstart',
    href: websitePaths.docs + '/quickstart',
    description:
      'Learn the basics of Grafbase and deploy to production in only a few small steps.',
    icon: QuickstartIcon,
  },
  {
    name: 'Community',
    href: websitePaths.community,
    description: 'Join the growing Grafbase community and start contributing.',
    icon: DocumentationIcon,
  },
  {
    name: 'Blog',
    href: websitePaths.blog,
    description:
      "Discover, learn and subscribe to all that's new with Grafbase.",
    icon: ChangelogIcon,
  },
  {
    name: 'Guides',
    href: websitePaths.guides,
    description:
      'Learn how to use Grafbase with frontend frameworks, external APIs, and more.',
    icon: GuidesIcon,
  },
  {
    name: 'Templates',
    href: websitePaths.templates,
    description:
      'Get started with Grafbase using a pre-built schema blueprint.',
    icon: ExamplesIcon,
  },
  {
    name: 'Examples',
    href: websitePaths.examples,
    description:
      'Clone ready-made examples with various frameworks, libraries, APIs, and more.',
    icon: GithubIcon,
  },
]

const solutionsLinks: NavbarLinkProps[] = [
  {
    name: 'Enterprise',
    href: websitePaths.enterprise,
    description: 'Grafbase enables enterprises to scale their API strategy.',
    icon: GlobeIcon,
  },
  {
    name: 'Government',
    href: websitePaths.government,
    description:
      'Grafbase is designed for privacy, security, and seamless integration within Government agencies.',
    icon: ShieldIcon,
  },
]

export const links: ({
  trigger?: boolean
  links?: NavbarLinkProps[]
  hidden?: boolean
} & NavbarLinkProps)[] = [
  {
    name: 'Product',
    href: '',
    trigger: true,
    links: featuresLinks,
  },
  {
    name: 'Use Cases',
    href: '',
    trigger: true,
    links: useCaseLinks,
  },
  {
    name: 'Alternatives',
    href: '',
    trigger: true,
    links: alternativeLinks,
  },
  {
    name: 'Resources',
    href: '',
    trigger: true,
    links: dropdownLinks,
  },
  {
    name: 'Solutions',
    href: '',
    trigger: true,
    links: solutionsLinks,
  },
  {
    name: 'Docs',
    href: websitePaths.docs,
  },
  {
    name: 'Pricing',
    href: websitePaths.pricing,
  },
  {
    name: 'Contact',
    href: websitePaths.contact,
  },
]
